import {Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {
  buildDataOutputSchema,
  buildOutputPorts,
  buildParameterOutputSchema,
  CATEGORY_ABBREVS,
  CATEGORY_COLORS,
  CATEGORY_ICONS,
  CATEGORY_LABELS,
  FLOW_IN_PORT,
  FLOW_OUT_PORT,
  FlowEdge,
  getNodeName,
  getNodePosition,
  mapCategory,
  NodePort,
  nodeSummary,
  remapReferences,
  resolveModuleIcon,
  setNodePosition,
  syncEdgesToManifest,
} from './workflow.models';
import {Manifest} from '../../api/model/manifest';
import {StepNode} from '../../api/model/stepNode';
import {ModuleManifest} from '../../api/model/moduleManifest';
import {CanvasSnapshot, ConnectionDraft, RenderedEdge, RenderedNode} from './workflow-canvas.models';
import {
  autoPanSpeed,
  buildBezierPath,
  calculateSvgBounds,
  computeAutoLayout,
  computeNodeHeight,
  getInputPortPoint,
  getOutputPortPoint,
  HEADER_HEIGHT,
  headerHeightFor,
  NODE_WIDTH,
  snapToGrid,
  ViewportRect,
} from './workflow-canvas.utils';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {NgClass} from '@angular/common';

@Component({
  selector: 'app-workflow-canvas',
  standalone: true,
  imports: [FaIconComponent, NgClass],
  templateUrl: './workflow-canvas.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./workflow-canvas.component.scss'],
})
export class WorkflowCanvasComponent implements OnInit, OnDestroy {
  private static readonly PASTE_OFFSET = 40;
  // ─── Connection Port Snapping ───────────────────────────────────────────────
  private static readonly PORT_SNAP_RADIUS = 40;
  private static readonly MAX_HISTORY = 50;
  /**
   * Mindestbewegung in Bildschirm-Pixeln, ab der ein Mousedown als Ziehen gilt.
   *
   * Ohne diese Schwelle machte JEDE einzelne `mousemove` zwischen Down und Up aus dem Klick ein
   * Ziehen (`didDrag`), womit die Selektion in `onCanvasMouseUp` ausfiel und sich die
   * Modul-Einstellungen nicht öffneten. In eingebetteten Webviews (z.B. geteilt in MS Teams)
   * liefert ein gewöhnlicher Klick regelmäßig so eine Mikrobewegung – dort war der Knoten
   * dadurch praktisch nicht anklickbar.
   */
  private static readonly DRAG_THRESHOLD_PX = 3;
  // ─── Auto-Panning beim Ziehen einer Kante ───────────────────────────────────
  /**
   * Breite des Randstreifens (Bildschirm-Pixel), in dem die Ansicht selbsttätig mitwandert.
   *
   * Ohne das ist ein Zielknoten außerhalb des Sichtfelds unerreichbar: Die Kante lässt sich nur
   * bis zum Rand ziehen, und ein `mouseleave` der Canvas bricht den Entwurf sogar ab
   * ({@link onCanvasMouseLeave}). Der Streifen liegt deshalb INNERHALB der Canvas – das Mitwandern
   * beginnt, bevor der Zeiger sie verlässt.
   */
  private static readonly AUTO_PAN_BAND_PX = 72;
  /** Höchstgeschwindigkeit am äußersten Rand des Streifens, in Bildschirm-Pixeln pro Sekunde. */
  private static readonly AUTO_PAN_MAX_SPEED = 900;
  /**
   * Deckel für den Zeitschritt eines Frames (Sekunden). Nach einem Tab-Wechsel oder einem
   * Layout-Ruckler liegen zwischen zwei Frames leicht mehrere hundert Millisekunden; ohne Deckel
   * würde die Ansicht dann in einem einzigen Sprung quer über das Diagramm schießen.
   */
  private static readonly AUTO_PAN_MAX_FRAME_SECONDS = 0.05;
  /**
   * Breite des rechts über der Canvas schwebenden Panels in Pixeln (0, wenn keins offen ist).
   *
   * Die Canvas liegt vollständig UNTER dem Panel: Ihr Rechteck reicht bis zum Fensterrand, der
   * rechte Teil ist aber verdeckt und mit der Maus nicht erreichbar. Ohne diesen Wert läge der
   * Randstreifen des Auto-Pannings unter dem Panel – man käme nie hinein, und das Mitwandern nach
   * rechts (die häufigste Richtung, weil Workflows nach rechts wachsen) bliebe wirkungslos.
   */
  @Input() rightOverlayWidth = 0;
  @Input() edges: FlowEdge[] = [];
  @Output() connectionCreated = new EventEmitter<{
    sourceNodeId: string;
    sourcePortIndex: number;
    targetNodeId: string;
    targetPortIndex: number
  }>();
  @Output() connectionDeleted = new EventEmitter<{ edgeId: string }>();
  @Output() nodeSelected = new EventEmitter<string | null>();
  @Input() manifest!: Manifest;
  @Output() nodeDeleted = new EventEmitter<string>();
  renderedNodes: RenderedNode[] = [];
  renderedEdges: RenderedEdge[] = [];
  svgMinX = -2000;
  svgMinY = -2000;
  svgWidth = 8000;
  svgHeight = 8000;
  panX = 0;
  panY = 0;
  zoom = 1;
  draggingNodeId: string | null = null;
  selectedNodeId: string | null = null;
  contextMenuNodeId: string | null = null;
  connectionDraft: ConnectionDraft | null = null;
  hoveredEdgeId: string | null = null;
  @Input() modules: ModuleManifest[] = [];
  @Input() readOnly = false;
  /** Step statuses for highlighting nodes during test execution (nodeId → status) */
  @Input() stepStatuses: { [nodeId: string]: string } = {};
  /** Markierung eines offenen chatBIC-Vorschlags (nodeId → neu/geändert). Laufstatus hat Vorrang. */
  @Input() proposalMarks: { [nodeId: string]: 'added' | 'changed' } = {};
  marquee: { x: number; y: number; w: number; h: number } | null = null;
  isMarqueeActive = false;
  @Output() nodeAdded = new EventEmitter<StepNode>();
  @Output() nodeDoubleClicked = new EventEmitter<string>();
  /** Ein abgelegter eingangsparameter wurde als Einstiegs-Trigger (wieder)hergestellt → Editor gleicht gegen das Set ab. */
  @Output() eingangTriggerDropped = new EventEmitter<void>();
  // ─── Multi-Selection ────────────────────────────────────────────────────────
  selectedNodeIds = new Set<string>();
  // ─── Snap-to-Grid ───────────────────────────────────────────────────────────
  snapEnabled = true;
  snappedPort: { nodeId: string; portIndex: number } | null = null;
  private marqueeStart: { x: number; y: number } | null = null;
  private panning = false;
  private panStartX = 0;
  private panStartY = 0;
  private draggingNode: RenderedNode | null = null;
  private dragOffsetX = 0;
  private dragOffsetY = 0;
  private didDrag = false;
  private nodeMap = new Map<string, RenderedNode>();
  private multiDragStartPositions = new Map<string, { x: number; y: number }>();
  private moduleMap = new Map<string, ModuleManifest>();
  // ─── Multi-drag offsets ─────────────────────────────────────────────────────
  private multiDragOffsets = new Map<string, { dx: number; dy: number }>();
  private redoStack: CanvasSnapshot[] = [];
  private dragStartPos: { x: number; y: number } | null = null;
  /** Zeigerposition beim Mousedown – Bezugspunkt für {@link DRAG_THRESHOLD_PX}. */
  private dragStartPointer: { x: number; y: number } | null = null;
  // ─── Auto-Panning ───────────────────────────────────────────────────────────
  /** Laufende `requestAnimationFrame`-Schleife des Auto-Pannings, sonst `null`. */
  private autoPanFrame: number | null = null;
  /** Geschwindigkeit der Schleife in Bildschirm-Pixeln pro Sekunde. */
  private autoPanVelocity = {x: 0, y: 0};
  /** Zeitstempel des letzten Frames – Grundlage des framerate-unabhängigen Zeitschritts. */
  private autoPanLastTs = 0;
  /**
   * Letzte bekannte Zeigerposition (Bildschirmkoordinaten). Während des Auto-Pannings bewegt sich
   * der Zeiger nicht, wohl aber die Ansicht unter ihm – der Kantenendpunkt muss deshalb aus DIESER
   * Position neu berechnet werden, sonst bleibt er im Diagramm kleben statt am Zeiger.
   */
  private autoPanPointer: { x: number; y: number } | null = null;
  /** Ob die Fenster-Listener für die Dauer eines Zugs registriert sind. */
  private trackingPointer = false;
  // ─── Clipboard (Copy & Paste) ───────────────────────────────────────────────
  private clipboard: { steps: StepNode[]; edges: FlowEdge[] } | null = null;
  // ─── Undo / Redo ────────────────────────────────────────────────────────────
  private undoStack: CanvasSnapshot[] = [];

  constructor(private el: ElementRef) {
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  get hasClipboard(): boolean {
    return this.clipboard !== null && this.clipboard.steps.length > 0;
  }

  get zoomPercent(): number {
    return Math.round(this.zoom * 100);
  }

  toggleSnap(): void {
    this.snapEnabled = !this.snapEnabled;
  }

  isPortSnapped(nodeId: string, portIndex: number): boolean {
    return !!this.snappedPort
      && this.snappedPort.nodeId === nodeId
      && this.snappedPort.portIndex === portIndex;
  }

  deleteSelectedNodes(): void {
    if (this.readOnly) return;
    if (this.selectedNodeIds.size === 0) return;
    this.pushSnapshot();
    const triggerId = this.manifest.trigger?.nodeId ?? 'trigger_0';
    const toDelete = [...this.selectedNodeIds];
    for (const nodeId of toDelete) {
      const node = this.nodeMap.get(nodeId);
      if (!node) continue;
      if (nodeId === triggerId) continue;

      // Clear variable references from other steps that referenced this node's outputs
      this.clearAllReferencesToNode(nodeId);

      // Mutate in place (not `this.edges = this.edges.filter(...)`) — reassigning would
      // detach this array from the same reference shared with the parent/sidebar panel.
      const kept = this.edges.filter(e => e.source !== nodeId && e.target !== nodeId);
      this.edges.length = 0;
      this.edges.push(...kept);
      const stepIdx = (this.manifest.steps ?? []).findIndex(s => s.nodeId === nodeId);
      if (stepIdx !== -1) this.manifest.steps!.splice(stepIdx, 1);
      const rnIdx = this.renderedNodes.findIndex(n => n.id === nodeId);
      if (rnIdx !== -1) this.renderedNodes.splice(rnIdx, 1);
      this.nodeMap.delete(nodeId);
      this.nodeDeleted.emit(nodeId);
    }
    this.selectedNodeIds.clear();
    this.selectedNodeId = null;
    this.nodeSelected.emit(null);
    this.recalculatePortConnections();
    this.rebuildEdges();
  }

  pasteClipboard(): void {
    if (this.readOnly) return;
    if (!this.clipboard || this.clipboard.steps.length === 0) return;
    this.pushSnapshot();

    const oldToNewId = new Map<string, string>();
    const pastedNodeIds: string[] = [];

    for (const srcStep of this.clipboard.steps) {
      const newId = `node_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
      oldToNewId.set(srcStep.nodeId!, newId);

      const newStep: StepNode = {
        ...JSON.parse(JSON.stringify(srcStep)),
        nodeId: newId,
      };
      // Offset position
      const pos = getNodePosition(newStep.inputs);
      if (!newStep.inputs) newStep.inputs = {};
      setNodePosition(newStep.inputs, pos.x + WorkflowCanvasComponent.PASTE_OFFSET, pos.y + WorkflowCanvasComponent.PASTE_OFFSET);
      // Append ' (Kopie)' to name
      const oldName = getNodeName(newStep.inputs, this.moduleMap.get(newStep.moduleId ?? ''));
      newStep.inputs['__name'] = oldName + ' (Kopie)';

      if (!this.manifest.steps) this.manifest.steps = [];
      this.manifest.steps.push(newStep);
      pastedNodeIds.push(newId);

      const rn = this.createRenderedNodeFromStep(newStep);
      // Reset port connections
      rn.inputPorts = rn.inputPorts.map(p => ({...p, connected: false}));
      rn.outputPorts = rn.outputPorts.map(p => ({...p, connected: false}));
      this.renderedNodes.push(rn);
      this.nodeMap.set(rn.id, rn);
      this.nodeAdded.emit(newStep);
    }

    for (const srcEdge of this.clipboard.edges) {
      const newSource = oldToNewId.get(srcEdge.source);
      const newTarget = oldToNewId.get(srcEdge.target);
      if (!newSource || !newTarget) continue;
      this.edges.push({
        id: `edge-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
        source: newSource, sourcePortIndex: srcEdge.sourcePortIndex,
        target: newTarget, targetPortIndex: srcEdge.targetPortIndex,
        label: srcEdge.label, type: srcEdge.type,
      });
    }

    // Wertbindungen der eingefügten Schritte auf die neuen Knoten-IDs umschreiben.
    //
    // `remapReferences` geht rekursiv über Strings, Listen UND Maps. Der frühere Code hier
    // behandelte nur skalare Referenzen und ersetzte per `String.replace` — Formel-Slots und
    // Variablen-Maps zeigten danach weiter auf die Originalknoten.
    for (const pastedId of pastedNodeIds) {
      const step = (this.manifest.steps ?? []).find(s => s.nodeId === pastedId);
      remapReferences(step?.inputs, oldToNewId);
    }

    this.recalculatePortConnections();
    this.rebuildEdges();
    this.updateSvgBounds();

    this.selectedNodeId = null;
    this.selectedNodeIds.clear();
    for (const id of pastedNodeIds) this.selectedNodeIds.add(id);
    this.nodeSelected.emit(null);

    // Shift clipboard positions for next paste
    for (const s of this.clipboard.steps) {
      const pos = getNodePosition(s.inputs);
      if (!s.inputs) s.inputs = {};
      setNodePosition(s.inputs, pos.x + WorkflowCanvasComponent.PASTE_OFFSET, pos.y + WorkflowCanvasComponent.PASTE_OFFSET);
    }
  }

  onInputPortMouseUp(e: MouseEvent, targetNode: RenderedNode, portIndex: number) {
    e.stopPropagation();
    e.preventDefault();
    if (this.readOnly) return;
    if (!this.connectionDraft) return;
    if (this.connectionDraft.sourceNodeId === targetNode.id) {
      this.connectionDraft = null;
      return;
    }

    const isDuplicate = this.edges.some(
      edge => edge.source === this.connectionDraft!.sourceNodeId
        && edge.sourcePortIndex === this.connectionDraft!.sourcePortIndex
        && edge.target === targetNode.id && edge.targetPortIndex === portIndex
    );
    if (!isDuplicate) {
      this.pushSnapshot();
      const newEdge: FlowEdge = {
        id: `edge-${Date.now()}`,
        source: this.connectionDraft.sourceNodeId, sourcePortIndex: this.connectionDraft.sourcePortIndex,
        target: targetNode.id, targetPortIndex: portIndex,
        label: '', type: 'success',
      };
      this.edges.push(newEdge);
      const sourceNode = this.nodeMap.get(this.connectionDraft.sourceNodeId);
      if (sourceNode?.outputPorts[this.connectionDraft.sourcePortIndex]) {
        sourceNode.outputPorts[this.connectionDraft.sourcePortIndex].connected = true;
      }
      if (targetNode.inputPorts[portIndex]) {
        targetNode.inputPorts[portIndex].connected = true;
      }
      // Eine Kante ist jetzt immer eine Flow-Kante — es gibt keine Referenz zu schreiben.
      // Werte werden im Konfig-Panel gebunden.
      this.rebuildEdges();
      this.connectionCreated.emit({
        sourceNodeId: this.connectionDraft.sourceNodeId,
        sourcePortIndex: this.connectionDraft.sourcePortIndex,
        targetNodeId: targetNode.id, targetPortIndex: portIndex,
      });
    }
    this.connectionDraft = null;
  }

  onEdgeMouseDown(e: MouseEvent, edge: RenderedEdge) {
    e.stopPropagation();
    e.preventDefault();
    if (this.readOnly) return;
    this.pushSnapshot();

    // Nur Flow-Kanten: in `inputs` ist nichts zu putzen.
    const idx = this.edges.findIndex(de => de.id === edge.id);
    if (idx !== -1) this.edges.splice(idx, 1);
    this.recalculatePortConnections();
    this.rebuildEdges();
    this.hoveredEdgeId = null;
    this.connectionDeleted.emit({edgeId: edge.id});
  }

  onDeleteNode(node: RenderedNode) {
    if (this.readOnly) return;
    this.pushSnapshot();
    this.contextMenuNodeId = null;

    // Clear variable references from other steps that referenced this node's outputs
    this.clearAllReferencesToNode(node.id);

    // Mutate in place (not `this.edges = this.edges.filter(...)`) — reassigning would
    // detach this array from the same reference shared with the parent/sidebar panel.
    const kept = this.edges.filter(e => e.source !== node.id && e.target !== node.id);
    this.edges.length = 0;
    this.edges.push(...kept);
    const stepIdx = (this.manifest.steps ?? []).findIndex(s => s.nodeId === node.id);
    if (stepIdx !== -1) this.manifest.steps!.splice(stepIdx, 1);
    const rnIdx = this.renderedNodes.findIndex(n => n.id === node.id);
    if (rnIdx !== -1) this.renderedNodes.splice(rnIdx, 1);
    this.nodeMap.delete(node.id);
    if (this.selectedNodeId === node.id) {
      this.selectedNodeId = null;
      this.nodeSelected.emit(null);
    }
    this.selectedNodeIds.delete(node.id);
    this.recalculatePortConnections();
    this.rebuildEdges();
    this.nodeDeleted.emit(node.id);
  }

  isNodeSelected(nodeId: string): boolean {
    return this.selectedNodeId === nodeId || this.selectedNodeIds.has(nodeId);
  }

  getNodeStatusClass(nodeId: string): string {
    const status = this.stepStatuses[nodeId];
    if (!status) {
      const mark = this.proposalMarks[nodeId];
      return mark === 'added' ? 'fc-node--proposal-added' : mark === 'changed' ? 'fc-node--proposal-changed' : '';
    }
    switch (status) {
      case 'COMPLETED':
        return 'fc-node--success';
      case 'FAILED':
        return 'fc-node--error';
      case 'RUNNING':
        return 'fc-node--running';
      default:
        return '';
    }
  }

  isEdgeConnectedToSelection(edge: RenderedEdge): boolean {
    if (this.selectedNodeId && (edge.sourceNodeId === this.selectedNodeId || edge.targetNodeId === this.selectedNodeId)) return true;
    if (this.selectedNodeIds.size > 0) {
      return this.selectedNodeIds.has(edge.sourceNodeId) || this.selectedNodeIds.has(edge.targetNodeId);
    }
    return false;
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(e: KeyboardEvent) {
    // Don't intercept keyboard shortcuts when user is typing in an input/textarea/contenteditable
    const active = document.activeElement as HTMLElement | null;
    const activeTag = active?.tagName?.toLowerCase();
    const isInInput = activeTag === 'input' || activeTag === 'textarea' || active?.isContentEditable ||
      active?.closest('bic-input, bic-select, bic-textarea');

    if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key === 'z') {
      if (isInInput) return;
      e.preventDefault();
      this.undo();
      return;
    }
    if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.shiftKey && e.key === 'Z'))) {
      if (isInInput) return;
      e.preventDefault();
      this.redo();
      return;
    }
    if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
      if (isInInput) return;
      e.preventDefault();
      this.selectAll();
      return;
    }
    if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
      if (isInInput) return;
      e.preventDefault();
      this.copySelection();
      return;
    }
    if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
      if (isInInput) return;
      e.preventDefault();
      this.pasteClipboard();
      return;
    }
    if ((e.ctrlKey || e.metaKey) && e.key === 'x') {
      if (isInInput) return;
      e.preventDefault();
      this.copySelection();
      this.deleteSelectedNodes();
      return;
    }
    if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
      if (isInInput) return;
      e.preventDefault();
      this.duplicateSelection();
      return;
    }
    if (e.key === 'Escape') {
      this.clearSelection();
      return;
    }
    if ((e.key === 'Delete' || e.key === 'Backspace') && (this.selectedNodeIds.size > 0 || this.selectedNodeId)) {
      if (isInInput) return;
      e.preventDefault();
      if (this.selectedNodeId && this.selectedNodeIds.size === 0) {
        this.selectedNodeIds.add(this.selectedNodeId);
        this.selectedNodeId = null;
      }
      this.deleteSelectedNodes();
      return;
    }
  }


  isPortEdgeHovered(nodeId: string, portType: 'input' | 'output', portIndex: number): boolean {
    if (!this.hoveredEdgeId) return false;
    const edge = this.renderedEdges.find(e => e.id === this.hoveredEdgeId);
    if (!edge) return false;
    return portType === 'output'
      ? edge.sourceNodeId === nodeId && edge.sourcePortIndex === portIndex
      : edge.targetNodeId === nodeId && edge.targetPortIndex === portIndex;
  }

  isPortEdgeSelected(nodeId: string, portType: 'input' | 'output', portIndex: number): boolean {
    const hasSelection = this.selectedNodeId || this.selectedNodeIds.size > 0;
    if (!hasSelection) return false;
    const connectedEdges = this.renderedEdges.filter(e => this.isEdgeConnectedToSelection(e));
    return connectedEdges.some(edge =>
      portType === 'output'
        ? edge.sourceNodeId === nodeId && edge.sourcePortIndex === portIndex
        : edge.targetNodeId === nodeId && edge.targetPortIndex === portIndex
    );
  }

  ngOnInit() {
    this.moduleMap = new Map(this.modules.map(m => [m.moduleId, m]));
    this.buildGraph();
    this.pushSnapshot();
  }

  ngOnDestroy() {
    // Ein laufendes Auto-Panning hält sonst über `requestAnimationFrame` eine Referenz auf die
    // zerstörte Komponente und läuft weiter, wenn der Editor während eines Zugs verlassen wird –
    // die Fenster-Listener ebenso.
    this.stopAutoPan();
    this.endPointerTracking();
  }

  selectAll(): void {
    this.selectedNodeId = null;
    this.selectedNodeIds.clear();
    for (const node of this.renderedNodes) {
      this.selectedNodeIds.add(node.id);
    }
  }

  // ─── Selection helpers ──────────────────────────────────────────────────────

  clearSelection(): void {
    this.selectedNodeId = null;
    this.selectedNodeIds.clear();
    this.nodeSelected.emit(null);
  }

  copySelection(): void {
    const selectedIds = this.getEffectiveSelection();
    if (selectedIds.size === 0) return;
    const triggerId = this.manifest.trigger?.nodeId ?? 'trigger_0';

    const copiedSteps: StepNode[] = [];
    for (const id of selectedIds) {
      if (id === triggerId) continue;
      const step = (this.manifest.steps ?? []).find(s => s.nodeId === id);
      if (step) {
        copiedSteps.push(JSON.parse(JSON.stringify(step)));
      }
    }
    if (copiedSteps.length === 0) return;

    const copiedNodeIds = new Set(copiedSteps.map(s => s.nodeId!));
    const copiedEdges: FlowEdge[] = this.edges
      .filter(e => copiedNodeIds.has(e.source) && copiedNodeIds.has(e.target))
      .map(e => ({...e}));

    this.clipboard = {steps: copiedSteps, edges: copiedEdges};
  }

  duplicateSelection(): void {
    this.copySelection();
    this.pasteClipboard();
  }

  // ─── Copy & Paste ───────────────────────────────────────────────────────────

  undo(): void {
    if (!this.canUndo) return;
    this.redoStack.push(this.createSnapshot());
    const snapshot = this.undoStack.pop()!;
    this.restoreSnapshot(snapshot);
  }

  redo(): void {
    if (!this.canRedo) return;
    this.undoStack.push(this.createSnapshot());
    const snapshot = this.redoStack.pop()!;
    this.restoreSnapshot(snapshot);
  }

  autoLayout(): void {
    this.pushSnapshot();
    const layoutEdges = this.edges.map(e => ({source: e.source, target: e.target}));
    const newPositions = computeAutoLayout(this.renderedNodes, layoutEdges);

    for (const [nodeId, pos] of newPositions) {
      const snapped = this.snap(pos.x, pos.y);
      const rn = this.nodeMap.get(nodeId);
      if (rn) {
        rn.x = snapped.x;
        rn.y = snapped.y;
      }
      this.syncPositionToManifest(nodeId, snapped.x, snapped.y);
    }

    this.rebuildEdges();
    this.updateSvgBounds();
    this.panX = 0;
    this.panY = 0;
    this.zoom = 1;
  }

  zoomIn(): void {
    this.zoomToCenter(Math.min(this.zoom + 0.15, 3));
  }

  // ─── Undo / Redo ────────────────────────────────────────────────────────────

  zoomOut(): void {
    this.zoomToCenter(Math.max(this.zoom - 0.15, 0.2));
  }

  zoomReset(): void {
    this.zoom = 1;
    this.panX = 0;
    this.panY = 0;
  }

  // ─── Auto-Layout ────────────────────────────────────────────────────────────

  fitToView(rightOffset = 0): void {
    if (this.renderedNodes.length === 0) return;
    const rect = this.el.nativeElement.getBoundingClientRect();
    const viewWidth = rect.width - rightOffset;
    const viewHeight = rect.height;
    const padding = 80;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const node of this.renderedNodes) {
      minX = Math.min(minX, node.x);
      minY = Math.min(minY, node.y);
      maxX = Math.max(maxX, node.x + node.width);
      maxY = Math.max(maxY, node.y + node.height);
    }
    const contentWidth = maxX - minX;
    const contentHeight = maxY - minY;
    if (contentWidth <= 0 || contentHeight <= 0) return;
    const scaleX = (viewWidth - padding * 2) / contentWidth;
    const scaleY = (viewHeight - padding * 2) / contentHeight;
    const newZoom = Math.min(Math.max(Math.min(scaleX, scaleY), 0.2), 3);
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    this.zoom = newZoom;
    this.panX = (viewWidth / 2) - centerX * newZoom;
    this.panY = viewHeight / 2 - centerY * newZoom;
  }

  // ─── Zoom ───────────────────────────────────────────────────────────────────

  onInputPortMouseDown(e: MouseEvent) {
    e.stopPropagation();
    e.preventDefault();
  }

  onOutputPortMouseDown(e: MouseEvent, node: RenderedNode, portIndex: number) {
    if (this.readOnly) return;
    // Nur von Flow-Ports lässt sich eine Kante ziehen. Wert-Ausgaben (Variable) zeigen an, WAS der
    // Knoten liefert; verbraucht werden sie im Konfig-Panel des Zielschritts. Ohne diese Sperre
    // entstünde eine Kante, die `syncEdgesToManifest` verwirft — sichtbar gezogen, beim Speichern
    // still verschwunden.
    if (!node.outputPorts[portIndex]?.isFlowPort) return;
    e.stopPropagation();
    e.preventDefault();
    this.hoveredEdgeId = null;
    const start = getOutputPortPoint(node, portIndex);
    this.connectionDraft = {
      sourceNodeId: node.id, sourcePortIndex: portIndex,
      sourcePortType: node.outputPorts[portIndex]?.dataType,
      startX: start.x, startY: start.y,
      currentX: start.x, currentY: start.y, path: '',
    };
    this.updateDraftPath();
    this.beginPointerTracking();
  }

  onNodeMouseDown(e: MouseEvent, node: RenderedNode) {
    if (this.connectionDraft) return;
    e.stopPropagation();
    e.preventDefault();
    this.contextMenuNodeId = null;
    this.beginPointerTracking();

    const isShift = e.shiftKey;
    const isAlreadyInMultiSelect = this.selectedNodeIds.has(node.id);

    if (isShift) {
      if (isAlreadyInMultiSelect) {
        this.selectedNodeIds.delete(node.id);
      } else {
        if (this.selectedNodeId && !this.selectedNodeIds.has(this.selectedNodeId)) {
          this.selectedNodeIds.add(this.selectedNodeId);
        }
        this.selectedNodeIds.add(node.id);
        this.selectedNodeId = null;
      }
      return;
    }

    if (isAlreadyInMultiSelect && this.selectedNodeIds.size > 1) {
      this.draggingNodeId = node.id;
      this.draggingNode = node;
      this.didDrag = false;
      this.dragStartPos = {x: node.x, y: node.y};
      this.dragStartPointer = {x: e.clientX, y: e.clientY};
      this.multiDragOffsets.clear();
      this.multiDragStartPositions.clear();
      for (const id of this.selectedNodeIds) {
        const n = this.nodeMap.get(id);
        if (n) {
          this.multiDragOffsets.set(id, {dx: n.x - node.x, dy: n.y - node.y});
          this.multiDragStartPositions.set(id, {x: n.x, y: n.y});
        }
      }
      this.dragOffsetX = (e.clientX - this.panX) / this.zoom - node.x;
      this.dragOffsetY = (e.clientY - this.panY) / this.zoom - node.y;
      return;
    }

    this.selectedNodeIds.clear();
    // Sofort selektieren statt erst beim Mouseup: das Einstellungs-Panel soll nicht davon
    // abhängen, dass ein `mouseup` auf der Canvas-Wurzel ankommt und `didDrag` false geblieben
    // ist (siehe DRAG_THRESHOLD_PX).
    this.selectedNodeId = node.id;
    this.nodeSelected.emit(node.id);
    this.draggingNodeId = node.id;
    this.draggingNode = node;
    this.didDrag = false;
    this.dragStartPos = {x: node.x, y: node.y};
    this.dragStartPointer = {x: e.clientX, y: e.clientY};
    this.dragOffsetX = (e.clientX - this.panX) / this.zoom - node.x;
    this.dragOffsetY = (e.clientY - this.panY) / this.zoom - node.y;
  }

  onCanvasMouseMove(e: MouseEvent) {
    if (this.connectionDraft) {
      this.autoPanPointer = {x: e.clientX, y: e.clientY};
      this.updateDraftFromPointer(e.clientX, e.clientY);
      this.updateAutoPan(e.clientX, e.clientY);
    } else if (this.draggingNode) {
      // Erst ab der Schwelle wird bewegt UND als Ziehen gewertet – darunter bleibt es ein Klick,
      // der den Knoten selektiert (siehe DRAG_THRESHOLD_PX).
      if (!this.didDrag) {
        if (!this.exceedsDragThreshold(e)) {
          return;
        }
        this.didDrag = true;
      }
      this.autoPanPointer = {x: e.clientX, y: e.clientY};
      this.applyNodeDrag(e.clientX, e.clientY);
      this.updateAutoPan(e.clientX, e.clientY);
    } else if (this.marqueeStart) {
      const diagramPos = this.screenToDiagram(e.clientX, e.clientY);
      const dx = Math.abs(diagramPos.x - this.marqueeStart.x);
      const dy = Math.abs(diagramPos.y - this.marqueeStart.y);
      if (!this.isMarqueeActive && (dx > 3 || dy > 3)) {
        this.isMarqueeActive = true;
      }
      if (this.isMarqueeActive) {
        const x = Math.min(this.marqueeStart.x, diagramPos.x);
        const y = Math.min(this.marqueeStart.y, diagramPos.y);
        this.marquee = {
          x,
          y,
          w: Math.abs(diagramPos.x - this.marqueeStart.x),
          h: Math.abs(diagramPos.y - this.marqueeStart.y)
        };
        this.updateMarqueeSelection();
      }
    } else if (this.panning) {
      this.panX = e.clientX - this.panStartX;
      this.panY = e.clientY - this.panStartY;
    }
  }

  onContextMenu(e: MouseEvent) {
    e.preventDefault();
  }

  onNodeDoubleClick(e: MouseEvent, node: RenderedNode) {
    e.stopPropagation();
    e.preventDefault();
    this.nodeDoubleClicked.emit(node.id);
  }

  onDrillDownNode(node: RenderedNode) {
    this.contextMenuNodeId = null;
    this.nodeDoubleClicked.emit(node.id);
  }

  onCanvasMouseDown(e: MouseEvent) {
    if (this.connectionDraft || this.draggingNode) return;
    e.preventDefault();
    this.contextMenuNodeId = null;

    if (e.button === 2) {
      const diagramPos = this.screenToDiagram(e.clientX, e.clientY);
      this.marqueeStart = {x: diagramPos.x, y: diagramPos.y};
      this.isMarqueeActive = false;
      this.marquee = null;
      if (!e.shiftKey) {
        this.selectedNodeId = null;
        this.selectedNodeIds.clear();
        this.nodeSelected.emit(null);
      }
      return;
    }

    if (this.selectedNodeId) {
      this.selectedNodeId = null;
      this.nodeSelected.emit(null);
    }
    if (!e.shiftKey) {
      this.selectedNodeIds.clear();
    }

    this.panning = true;
    this.panStartX = e.clientX - this.panX;
    this.panStartY = e.clientY - this.panY;
  }

  onCanvasMouseUp() {
    // Multi-drag undo snapshot
    if (this.draggingNode && this.didDrag && this.selectedNodeIds.size > 1 && this.multiDragStartPositions.size > 0) {
      let moved = false;
      for (const [id, start] of this.multiDragStartPositions) {
        const n = this.nodeMap.get(id);
        if (n && (Math.abs(n.x - start.x) > 2 || Math.abs(n.y - start.y) > 2)) {
          moved = true;
          break;
        }
      }
      if (moved) {
        const currentPositions = new Map<string, { x: number; y: number }>();
        for (const [id,] of this.multiDragStartPositions) {
          const n = this.nodeMap.get(id);
          if (n) currentPositions.set(id, {x: n.x, y: n.y});
        }
        for (const [id, start] of this.multiDragStartPositions) {
          const n = this.nodeMap.get(id);
          if (n) {
            n.x = start.x;
            n.y = start.y;
          }
        }
        this.undoStack.push(this.createSnapshot());
        if (this.undoStack.length > WorkflowCanvasComponent.MAX_HISTORY) this.undoStack.shift();
        this.redoStack.length = 0;
        for (const [id, pos] of currentPositions) {
          const n = this.nodeMap.get(id);
          if (n) {
            n.x = pos.x;
            n.y = pos.y;
          }
        }
        // Sync all moved positions to manifest
        for (const [id,] of this.multiDragStartPositions) {
          const n = this.nodeMap.get(id);
          if (n) this.syncPositionToManifest(id, n.x, n.y);
        }
        this.rebuildEdges();
      }
      this.multiDragOffsets.clear();
      this.multiDragStartPositions.clear();
    }
    // Single-drag undo snapshot
    else if (this.draggingNode && this.didDrag && this.dragStartPos) {
      const dx = Math.abs(this.draggingNode.x - this.dragStartPos.x);
      const dy = Math.abs(this.draggingNode.y - this.dragStartPos.y);
      if (dx > 2 || dy > 2) {
        const currentX = this.draggingNode.x;
        const currentY = this.draggingNode.y;
        this.draggingNode.x = this.dragStartPos.x;
        this.draggingNode.y = this.dragStartPos.y;
        this.undoStack.push(this.createSnapshot());
        if (this.undoStack.length > WorkflowCanvasComponent.MAX_HISTORY) this.undoStack.shift();
        this.redoStack.length = 0;
        this.draggingNode.x = currentX;
        this.draggingNode.y = currentY;
        this.syncPositionToManifest(this.draggingNode.id, currentX, currentY);
        this.rebuildEdges();
      }
    }

    // Finalize marquee
    if (this.isMarqueeActive && this.marquee) {
      this.updateMarqueeSelection();
      this.marquee = null;
      this.marqueeStart = null;
      this.isMarqueeActive = false;
      this.panning = false;
      this.draggingNodeId = null;
      this.draggingNode = null;
      this.didDrag = false;
      this.dragStartPos = null;
      this.dragStartPointer = null;
      this.stopAutoPan();
      this.endPointerTracking();
      this.autoPanPointer = null;
      return;
    }

    // Klick ohne Ziehen auf einen Knoten aus einer Mehrfachauswahl: reduziert die Auswahl auf
    // diesen Knoten. Der Einzelklick-Fall selektiert bereits im Mousedown, daher der Id-Vergleich
    // gegen ein doppeltes Emit.
    if (this.draggingNode && !this.didDrag && this.selectedNodeId !== this.draggingNode.id) {
      this.selectedNodeId = this.draggingNode.id;
      this.selectedNodeIds.clear();
      this.nodeSelected.emit(this.draggingNode.id);
    }

    // Connection port-snap
    if (this.connectionDraft && this.snappedPort) {
      const targetNode = this.nodeMap.get(this.snappedPort.nodeId);
      if (targetNode) {
        this.onInputPortMouseUp(new MouseEvent('mouseup'), targetNode, this.snappedPort.portIndex);
      }
    }
    this.snappedPort = null;

    this.stopAutoPan();
    this.endPointerTracking();
    this.autoPanPointer = null;
    this.connectionDraft = null;
    this.draggingNodeId = null;
    this.draggingNode = null;
    this.didDrag = false;
    this.dragStartPos = null;
    this.dragStartPointer = null;
    this.panning = false;
    this.marquee = null;
    this.marqueeStart = null;
    this.isMarqueeActive = false;
    this.multiDragOffsets.clear();
    this.multiDragStartPositions.clear();
  }

  // ─── Port interactions ──────────────────────────────────────────────────────

  /** True, sobald der Zeiger sich weiter als {@link DRAG_THRESHOLD_PX} vom Mousedown entfernt hat. */
  private exceedsDragThreshold(e: MouseEvent): boolean {
    if (!this.dragStartPointer) {
      return true; // kein Bezugspunkt (z.B. synthetisches Event) → wie bisher als Ziehen behandeln
    }
    const dx = Math.abs(e.clientX - this.dragStartPointer.x);
    const dy = Math.abs(e.clientY - this.dragStartPointer.y);
    return Math.max(dx, dy) > WorkflowCanvasComponent.DRAG_THRESHOLD_PX;
  }

  onCanvasMouseLeave() {
    // Während eines Zugs NICHT abbrechen: Sonst endet genau der Fall, für den das Auto-Panning
    // gedacht ist – der Zeiger verlässt die Canvas auf dem Weg zu einem Knoten außerhalb des
    // Sichtfelds. Ab hier übernehmen die Fenster-Listener (siehe beginPointerTracking).
    if (this.connectionDraft || this.draggingNode) return;
    this.onCanvasMouseUp();
  }

  onWheel(e: WheelEvent) {
    e.preventDefault();
    const rect = this.el.nativeElement.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const oldZoom = this.zoom;
    const delta = e.deltaY > 0 ? -0.05 : 0.05;
    const newZoom = Math.min(Math.max(oldZoom + delta, 0.2), 3);
    this.panX = mouseX - (mouseX - this.panX) * (newZoom / oldZoom);
    this.panY = mouseY - (mouseY - this.panY) * (newZoom / oldZoom);
    this.zoom = newZoom;
    this.updateSvgBounds();
  }

  // ─── Edge interaction ───────────────────────────────────────────────────────

  onDragOver(e: DragEvent) {
    if (e.dataTransfer?.types.includes('application/workflow-module')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    }
  }

  // ─── Node dragging ──────────────────────────────────────────────────────────

  onDrop(e: DragEvent) {
    e.preventDefault();
    if (this.readOnly) return;
    const json = e.dataTransfer?.getData('application/workflow-module');
    if (!json) return;
    try {
      const mod: ModuleManifest = JSON.parse(json);
      const pos = this.screenToDiagram(e.clientX, e.clientY);
      this.addNodeFromModule(mod, pos.x, pos.y);
    } catch { /* ignore invalid data */
    }
  }

  // ─── Canvas events ──────────────────────────────────────────────────────────

  addNodeFromModule(mod: ModuleManifest, x: number, y: number): void {
    if (this.readOnly) return;
    this.pushSnapshot();
    const snapped = this.snap(x, y);
    x = snapped.x;
    y = snapped.y;

    const category = mapCategory(mod.category);

    // ── eingangsparameter = Einstiegs-/Trigger-Knoten (genau einer je Regel, set-getrieben) ──
    // Statt eines frei platzierten Steps mit rohen Modul-Ausgängen wird der Einstiegs-Trigger
    // (wieder)hergestellt: vorhandene Parameter bleiben erhalten, Ausgänge sind param-basiert.
    // Der Editor gleicht anschließend gegen die Regelset-Parameter ab (eingangTriggerDropped).
    if ((mod.moduleId ?? '').toLowerCase() === 'eingangsparameter') {
      const oldTrigger = this.manifest.trigger;
      const oldWasEingang = (oldTrigger?.moduleId ?? '').toLowerCase() === 'eingangsparameter';
      const prev: any = oldWasEingang ? (oldTrigger?.config ?? {}) : {};
      const config: any = {
        triggerType: mod.moduleId,
        __name: mod.name,
        __ui: {position: {x, y}},
        parameter: Array.isArray(prev.parameter) ? prev.parameter : [],
        pruefebene: prev.pruefebene ?? 'lv_position',
        identifikatorParamId: prev.identifikatorParamId ?? '',
      };
      config.outputSchema = buildParameterOutputSchema(config);
      this.manifest.trigger = {
        nodeId: oldTrigger?.nodeId ?? 'trigger_0',
        moduleId: mod.moduleId,
        moduleVersion: mod.version,
        config,
        onSuccess: oldTrigger?.onSuccess,
      };
      this.buildGraph();
      this.updateSvgBounds();
      this.selectedNodeId = this.manifest.trigger.nodeId!;
      this.selectedNodeIds.clear();
      this.nodeSelected.emit(this.manifest.trigger.nodeId!);
      this.eingangTriggerDropped.emit();
      return;
    }

    // ── TRIGGER modules replace the existing trigger ──
    if (category === 'TRIGGER') {
      const oldTriggerId = this.manifest.trigger?.nodeId ?? 'trigger_0';
      // Trigger: kein Flow-In (er startet den Ablauf), genau ein Flow-Out.
      const outputPorts: NodePort[] = [{...FLOW_OUT_PORT}];

      this.manifest.trigger = {
        nodeId: oldTriggerId,
        moduleId: mod.moduleId,
        moduleVersion: mod.version,
        config: {
          triggerType: mod.moduleId,
          __name: mod.name,
          __ui: {position: {x, y}},
        },
        onSuccess: this.manifest.trigger?.onSuccess,
      };

      const existingRn = this.nodeMap.get(oldTriggerId);
      if (existingRn) {
        existingRn.label = mod.name;
        existingRn.category = category;
        existingRn.categoryLabel = CATEGORY_LABELS[category] ?? category;
        existingRn.categoryAbbrev = CATEGORY_ABBREVS[category] ?? '?';
        existingRn.categoryColor = CATEGORY_COLORS[category] ?? '#6B7280';
        existingRn.nodeIcon = resolveModuleIcon(mod.icon) ?? CATEGORY_ICONS[category] ?? null;
        existingRn.x = x;
        existingRn.y = y;
        existingRn.inputPorts = [];
        existingRn.outputPorts = outputPorts;
        existingRn.height = computeNodeHeight([], outputPorts);
        existingRn.stepRef = null;
      } else {
        // No existing rendered node – create a new one
        const newRn: RenderedNode = {
          id: oldTriggerId,
          label: mod.name,
          category,
          categoryLabel: CATEGORY_LABELS[category] ?? category,
          categoryAbbrev: CATEGORY_ABBREVS[category] ?? '?',
          categoryColor: CATEGORY_COLORS[category] ?? '#6B7280',
          nodeIcon: resolveModuleIcon(mod.icon) ?? CATEGORY_ICONS[category] ?? null,
          x, y,
          width: NODE_WIDTH,
          height: computeNodeHeight([], outputPorts),
          headerHeight: HEADER_HEIGHT,
          summary: null,
          inputPorts: [],
          outputPorts,
          stepRef: null,
        };
        this.renderedNodes.push(newRn);
        this.nodeMap.set(oldTriggerId, newRn);
      }

      this.rebuildEdges();
      this.updateSvgBounds();
      this.selectedNodeId = oldTriggerId;
      this.selectedNodeIds.clear();
      this.nodeSelected.emit(oldTriggerId);
      return;
    }

    // ── Regular (non-trigger) modules are added as step nodes ──
    const newStep: StepNode = {
      nodeId: `node_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
      moduleId: mod.moduleId,
      moduleVersion: mod.version,
      inputs: {
        __name: mod.name,
        __ui: {position: {x, y}},
      },
      onSuccess: undefined,
      onError: undefined,
    };

    if (!this.manifest.steps) this.manifest.steps = [];
    this.manifest.steps.push(newStep);

    const rn = this.createRenderedNodeFromStep(newStep);
    this.renderedNodes.push(rn);
    this.nodeMap.set(rn.id, rn);
    this.updateSvgBounds();

    this.selectedNodeId = newStep.nodeId!;
    this.selectedNodeIds.clear();
    this.nodeSelected.emit(newStep.nodeId!);
    this.nodeAdded.emit(newStep);
  }

  onMenuMouseDown(e: MouseEvent, node: RenderedNode) {
    e.stopPropagation();
    e.preventDefault();
    this.contextMenuNodeId = this.contextMenuNodeId === node.id ? null : node.id;
  }

  /** Rebuild the visual graph from the current manifest/edges. Can be called externally. */
  buildGraph() {
    const nodeMap = new Map<string, RenderedNode>();
    const trigger = this.manifest.trigger;

    if (trigger) {
      const triggerMod = this.moduleMap.get(trigger.moduleId ?? '');
      const category = 'TRIGGER';
      const pos = getNodePosition(trigger.config);
      const name = getNodeName(trigger.config, triggerMod);
      // Trigger: kein Flow-In (er startet den Ablauf), genau ein Flow-Out. Seine Ausgaben stehen
      // nicht mehr als Ports am Knoten — sie erscheinen im Panel des Zielschritts als wählbare Werte.
      const outputPorts: NodePort[] = [{...FLOW_OUT_PORT}];

      const triggerNode: RenderedNode = {
        id: trigger.nodeId ?? 'trigger_0',
        label: name,
        category,
        categoryLabel: CATEGORY_LABELS[category],
        categoryAbbrev: CATEGORY_ABBREVS[category],
        categoryColor: CATEGORY_COLORS[category],
        nodeIcon: resolveModuleIcon(triggerMod?.icon) ?? CATEGORY_ICONS[category] ?? null,
        x: pos.x, y: pos.y,
        width: NODE_WIDTH,
        height: computeNodeHeight([], outputPorts),
        headerHeight: HEADER_HEIGHT,
        summary: null,
        inputPorts: [],
        outputPorts,
        stepRef: null,
      };
      this.renderedNodes.push(triggerNode);
      nodeMap.set(triggerNode.id, triggerNode);
    }

    const defaultSpacingX = 300;
    for (const [idx, step] of (this.manifest.steps ?? []).entries()) {
      const rn = this.createRenderedNodeFromStep(step);
      // Auto-assign positions if not stored
      if (rn.x === 0 && rn.y === 0) {
        rn.x = (idx + 1) * defaultSpacingX + 44;
        rn.y = 220;
        if (!step.inputs) step.inputs = {};
        setNodePosition(step.inputs, rn.x, rn.y);
      }
      this.renderedNodes.push(rn);
      nodeMap.set(rn.id, rn);
    }

    this.nodeMap = nodeMap;
    this.recalculatePortConnections();
    this.rebuildEdges();
    this.updateSvgBounds();
  }

  private snap(x: number, y: number): { x: number; y: number } {
    return this.snapEnabled
      ? {x: snapToGrid(x), y: snapToGrid(y)}
      : {x, y};
  }

  /**
   * Schnappschuss VOR einer Änderung im Konfig-Panel.
   *
   * Nötig, seit Wertbindungen dort gesetzt werden statt per Kante: der Schnappschuss enthält eine
   * Tiefkopie der Schritte samt `inputs`. Ohne diesen Aufruf würde Strg+Z eine Panel-Eingabe
   * stillschweigend zurückdrehen (weil der letzte Schnappschuss noch den alten Wert trägt) oder
   * eine gerade gesetzte Bindung überleben lassen.
   */
  pushSnapshotForPanel(): void {
    this.pushSnapshot();
  }

  // ─── Drag & Drop from sidebar ───────────────────────────────────────────────

  private getEffectiveSelection(): Set<string> {
    if (this.selectedNodeIds.size > 0) return this.selectedNodeIds;
    if (this.selectedNodeId) return new Set([this.selectedNodeId]);
    return new Set();
  }

  private zoomToCenter(newZoom: number): void {
    const rect = this.el.nativeElement.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const oldZoom = this.zoom;
    this.panX = centerX - (centerX - this.panX) * (newZoom / oldZoom);
    this.panY = centerY - (centerY - this.panY) * (newZoom / oldZoom);
    this.zoom = newZoom;
  }

  /** Clear all ${nodeId.outputs.*} references from every step that points to the given node */
  private clearAllReferencesToNode(nodeId: string): void {
    const pattern = `\${${nodeId}.outputs.`;
    for (const step of this.manifest.steps ?? []) {
      if (!step.inputs) continue;
      for (const key of Object.keys(step.inputs)) {
        const val = step.inputs[key];
        if (typeof val === 'string' && val.includes(pattern)) {
          delete step.inputs[key];
        } else if (Array.isArray(val)) {
          // Array inputs (e.g. formula `variablen`): null the matching slots, keeping indices stable.
          const cleared = val.map((el: any) => (typeof el === 'string' && el.includes(pattern)) ? null : el);
          if (cleared.every((el: any) => el == null)) {
            delete step.inputs[key];
          } else {
            step.inputs[key] = cleared;
          }
        } else if (val && typeof val === 'object') {
          // Object-map inputs (e.g. Nutzerantwort `variablen`): drop entries referencing the node.
          for (const k of Object.keys(val)) {
            if (typeof val[k] === 'string' && val[k].includes(pattern)) delete val[k];
          }
          if (Object.keys(val).length === 0) delete step.inputs[key];
        }
      }
    }
  }

  // ─── Marquee helpers ────────────────────────────────────────────────────────

  private updateMarqueeSelection(): void {
    if (!this.marquee) return;
    const {x, y, w, h} = this.marquee;
    this.selectedNodeIds.clear();
    this.selectedNodeId = null;
    for (const node of this.renderedNodes) {
      const nodeRight = node.x + node.width;
      const nodeBottom = node.y + node.height;
      if (node.x < x + w && nodeRight > x && node.y < y + h && nodeBottom > y) {
        this.selectedNodeIds.add(node.id);
      }
    }
  }

  // ─── Snapshot helpers ───────────────────────────────────────────────────────

  private pushSnapshot(): void {
    this.undoStack.push(this.createSnapshot());
    if (this.undoStack.length > WorkflowCanvasComponent.MAX_HISTORY) this.undoStack.shift();
    this.redoStack.length = 0;
  }

  /**
   * Rechnet Zusammenfassung und Höhe eines Knotens neu.
   *
   * `nodeSummary` entsteht sonst nur beim Aufbau des Knotens — eine Panel-Änderung würde die Karte
   * nicht aktualisieren. Da die Karte nach dem Wegfall der Datenkanten das einzige Signal auf der
   * Leinwand ist, welche Werte ein Schritt verwendet, muss sie mitlaufen.
   */
  refreshNode(nodeId: string | null | undefined): void {
    if (!nodeId) return;
    const node = this.renderedNodes.find(n => n.id === nodeId);
    const step = (this.manifest?.steps ?? []).find(s => s.nodeId === nodeId);
    if (!node || !step) return;

    const mod = this.moduleMap.get(step.moduleId ?? '');
    node.summary = nodeSummary(step, mod, ref => this.referenceLabel(ref));
    node.headerHeight = headerHeightFor(!!node.summary);
    node.height = computeNodeHeight(node.inputPorts, node.outputPorts, node.headerHeight);
    this.rebuildEdges();
  }

  /**
   * Nächster Flow-Eingang im Snap-Radius. Ohne Datenports hat jeder Knoten genau einen Eingang,
   * eine Typprüfung gibt es nicht mehr — eine Flow-Kante passt immer.
   */
  private findNearestCompatiblePort(
    x: number, y: number
  ): { nodeId: string; portIndex: number; px: number; py: number } | null {
    if (!this.connectionDraft) return null;

    let best: { nodeId: string; portIndex: number; px: number; py: number; dist: number } | null = null;

    for (const node of this.renderedNodes) {
      if (node.id === this.connectionDraft.sourceNodeId) continue;

      for (let i = 0; i < node.inputPorts.length; i++) {
        const pt = getInputPortPoint(node, i);
        const dx = pt.x - x;
        const dy = pt.y - y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist <= WorkflowCanvasComponent.PORT_SNAP_RADIUS && (!best || dist < best.dist)) {
          best = {nodeId: node.id, portIndex: i, px: pt.x, py: pt.y, dist};
        }
      }
    }

    return best ? {nodeId: best.nodeId, portIndex: best.portIndex, px: best.px, py: best.py} : null;
  }

  private createSnapshot(): CanvasSnapshot {
    return {
      nodePositions: Object.fromEntries(
        this.renderedNodes.map(n => [n.id, {x: n.x, y: n.y}])
      ),
      edges: this.edges.map(e => ({...e})),
      workflowNodeIds: (this.manifest.steps ?? []).map(s => s.nodeId!),
      workflowNodes: (this.manifest.steps ?? []).map(s => JSON.parse(JSON.stringify(s))),
    };
  }

  private restoreSnapshot(snapshot: CanvasSnapshot): void {
    const currentNodeIds = new Set((this.manifest.steps ?? []).map(s => s.nodeId!));
    const snapshotNodeIds = new Set(snapshot.workflowNodeIds);

    // Remove nodes not in snapshot
    for (const nodeId of currentNodeIds) {
      if (!snapshotNodeIds.has(nodeId)) {
        const stepIdx = (this.manifest.steps ?? []).findIndex(s => s.nodeId === nodeId);
        if (stepIdx !== -1) this.manifest.steps!.splice(stepIdx, 1);
        const rnIdx = this.renderedNodes.findIndex(n => n.id === nodeId);
        if (rnIdx !== -1) this.renderedNodes.splice(rnIdx, 1);
        this.nodeMap.delete(nodeId);
      }
    }

    // Restore nodes from snapshot
    for (const snapshotNode of snapshot.workflowNodes) {
      if (!currentNodeIds.has(snapshotNode.nodeId)) {
        const restored: StepNode = JSON.parse(JSON.stringify(snapshotNode));
        if (!this.manifest.steps) this.manifest.steps = [];
        this.manifest.steps.push(restored);
        const rn = this.createRenderedNodeFromStep(restored);
        this.renderedNodes.push(rn);
        this.nodeMap.set(rn.id, rn);
      }
    }

    // Restore positions
    for (const [nodeId, pos] of Object.entries(snapshot.nodePositions)) {
      const rn = this.nodeMap.get(nodeId);
      if (rn) {
        rn.x = pos.x;
        rn.y = pos.y;
      }
      this.syncPositionToManifest(nodeId, pos.x, pos.y);
    }

    this.edges.length = 0;
    for (const e of snapshot.edges) {
      this.edges.push({...e});
    }

    this.recalculatePortConnections();
    this.rebuildEdges();
    this.updateSvgBounds();

    this.selectedNodeIds.clear();
    if (this.selectedNodeId && !this.nodeMap.has(this.selectedNodeId)) {
      this.selectedNodeId = null;
      this.nodeSelected.emit(null);
    }
  }

  // ─── Private helpers ────────────────────────────────────────────────────────

  private updateSvgBounds(): void {
    const bounds = calculateSvgBounds(this.renderedNodes);
    this.svgMinX = bounds.minX;
    this.svgMinY = bounds.minY;
    this.svgWidth = bounds.width;
    this.svgHeight = bounds.height;
  }

  private screenToDiagram(clientX: number, clientY: number) {
    const rect = this.el.nativeElement.getBoundingClientRect();
    return {
      x: (clientX - rect.left - this.panX) / this.zoom,
      y: (clientY - rect.top - this.panY) / this.zoom,
    };
  }

  private updateDraftPath() {
    if (!this.connectionDraft) return;
    this.connectionDraft.path = buildBezierPath(
      this.connectionDraft.startX, this.connectionDraft.startY,
      this.connectionDraft.currentX, this.connectionDraft.currentY,
    );
  }

  /**
   * Verschiebt den gezogenen Knoten (und bei Mehrfachauswahl die mitgezogenen) auf die
   * Zeigerposition.
   *
   * Wie {@link updateDraftFromPointer} bewusst aus Bildschirmkoordinaten gerechnet: Während des
   * Auto-Pannings ändert sich `panX`/`panY`, ohne dass sich der Zeiger bewegt – nur die erneute
   * Umrechnung hält den Knoten unter dem Cursor statt ihn mit der Ansicht wegwandern zu lassen.
   */
  private applyNodeDrag(clientX: number, clientY: number): void {
    if (!this.draggingNode) return;
    const snapped = this.snap(
      (clientX - this.panX) / this.zoom - this.dragOffsetX,
      (clientY - this.panY) / this.zoom - this.dragOffsetY,
    );

    if (this.selectedNodeIds.size > 1 && this.selectedNodeIds.has(this.draggingNode.id)) {
      this.draggingNode.x = snapped.x;
      this.draggingNode.y = snapped.y;
      for (const [id, offset] of this.multiDragOffsets) {
        if (id === this.draggingNode.id) continue;
        const n = this.nodeMap.get(id);
        if (n) {
          n.x = snapped.x + offset.dx;
          n.y = snapped.y + offset.dy;
        }
      }
    } else {
      this.draggingNode.x = snapped.x;
      this.draggingNode.y = snapped.y;
    }
    this.rebuildEdges();
    this.updateSvgBounds();
  }

  // ─── Auto-Panning beim Ziehen ───────────────────────────────────────────────

  /**
   * Setzt den Endpunkt des Kantenentwurfs auf die Zeigerposition – bzw. auf den nächstgelegenen
   * kompatiblen Port, wenn einer in Reichweite ist.
   *
   * Bewusst aus Bildschirmkoordinaten heraus gerechnet und nicht aus dem letzten Diagrammpunkt:
   * Das Auto-Panning verschiebt die Ansicht, ohne dass der Zeiger sich bewegt. Nur die erneute
   * Umrechnung hält den Endpunkt am Zeiger und lässt ihn an Ports schnappen, die während des
   * Mitwanderns neu ins Bild kommen.
   */
  private updateDraftFromPointer(clientX: number, clientY: number): void {
    if (!this.connectionDraft) return;
    const pos = this.screenToDiagram(clientX, clientY);
    const nearestPort = this.findNearestCompatiblePort(pos.x, pos.y);
    if (nearestPort) {
      this.connectionDraft.currentX = nearestPort.px;
      this.connectionDraft.currentY = nearestPort.py;
      this.snappedPort = {nodeId: nearestPort.nodeId, portIndex: nearestPort.portIndex};
    } else {
      this.connectionDraft.currentX = pos.x;
      this.connectionDraft.currentY = pos.y;
      this.snappedPort = null;
    }
    this.updateDraftPath();
  }

  /**
   * Bestimmt aus der Zeigerposition, ob und wie schnell die Ansicht mitwandern soll, und startet
   * bzw. stoppt die Schleife entsprechend.
   *
   * Die Geschwindigkeit wächst linear mit der Eindringtiefe in den Randstreifen: am inneren Rand
   * null, außen {@link AUTO_PAN_MAX_SPEED}. Ein harter Ein/Aus-Schalter würde die Ansicht
   * springen lassen, sobald der Zeiger die Grenze streift.
   */
  private updateAutoPan(clientX: number, clientY: number): void {
    this.autoPanVelocity = autoPanSpeed(
      this.visibleViewportRect(),
      clientX,
      clientY,
      WorkflowCanvasComponent.AUTO_PAN_BAND_PX,
      WorkflowCanvasComponent.AUTO_PAN_MAX_SPEED,
    );

    if (this.autoPanVelocity.x === 0 && this.autoPanVelocity.y === 0) {
      this.stopAutoPan();
      return;
    }
    this.startAutoPan();
  }

  /**
   * Der tatsächlich sichtbare Ausschnitt: das Canvas-Rechteck abzüglich des rechts darüber
   * schwebenden Panels. Bezugsgröße für den Randstreifen des Auto-Pannings.
   */
  private visibleViewportRect(): ViewportRect {
    const rect: DOMRect = this.el.nativeElement.getBoundingClientRect();
    const right = Math.max(rect.left, rect.right - Math.max(0, this.rightOverlayWidth));
    return {left: rect.left, top: rect.top, right, bottom: rect.bottom};
  }

  private startAutoPan(): void {
    if (this.autoPanFrame !== null) return;
    this.autoPanLastTs = 0;
    this.autoPanFrame = requestAnimationFrame(ts => this.autoPanTick(ts));
  }

  private autoPanTick(timestamp: number): void {
    this.autoPanFrame = null;
    // Ohne aktiven Zug gibt es nichts mitzuführen – die Schleife endet still.
    if (!this.autoPanPointer) return;
    if (!this.connectionDraft && !(this.draggingNode && this.didDrag)) return;

    if (this.autoPanLastTs === 0) {
      // Erster Frame: nur den Bezugszeitpunkt setzen, sonst wäre der Zeitschritt der gesamte
      // Abstand zum Seitenstart.
      this.autoPanLastTs = timestamp;
    }
    const seconds = Math.min(
      (timestamp - this.autoPanLastTs) / 1000,
      WorkflowCanvasComponent.AUTO_PAN_MAX_FRAME_SECONDS,
    );
    this.autoPanLastTs = timestamp;

    this.panX += this.autoPanVelocity.x * seconds;
    this.panY += this.autoPanVelocity.y * seconds;
    if (this.connectionDraft) {
      this.updateDraftFromPointer(this.autoPanPointer.x, this.autoPanPointer.y);
    } else {
      this.applyNodeDrag(this.autoPanPointer.x, this.autoPanPointer.y);
    }

    this.autoPanFrame = requestAnimationFrame(ts => this.autoPanTick(ts));
  }

  private stopAutoPan(): void {
    if (this.autoPanFrame !== null) {
      cancelAnimationFrame(this.autoPanFrame);
      this.autoPanFrame = null;
    }
    this.autoPanVelocity = {x: 0, y: 0};
    this.autoPanLastTs = 0;
  }

  // ─── Zeigerverfolgung außerhalb der Canvas ──────────────────────────────────

  /**
   * Verfolgt den Zeiger für die Dauer eines Zugs auch AUSSERHALB der Canvas.
   *
   * Ohne das reißt genau der Fall ab, für den das Auto-Panning gedacht ist: Wer zu einem Knoten
   * außerhalb des Sichtfelds zieht, bewegt die Maus zügig – und überspringt den 72&nbsp;px breiten
   * Randstreifen dabei mühelos in einem einzigen `mousemove`. Danach liegt der Zeiger außerhalb
   * der Canvas, es kommt kein Event mehr an, und `mouseleave` beendet den Zug sogar.
   *
   * Die Listener hängen nur während eines Zugs am Fenster; über der Canvas laufen sie zusätzlich
   * zum Template-Binding, was unschädlich ist: beide Pfade rechnen die Position absolut aus der
   * Zeigerposition, wiederholte Anwendung ändert das Ergebnis also nicht.
   */
  private beginPointerTracking(): void {
    if (this.trackingPointer) return;
    this.trackingPointer = true;
    window.addEventListener('mousemove', this.onWindowMouseMove);
    window.addEventListener('mouseup', this.onWindowMouseUp);
  }

  private endPointerTracking(): void {
    if (!this.trackingPointer) return;
    this.trackingPointer = false;
    window.removeEventListener('mousemove', this.onWindowMouseMove);
    window.removeEventListener('mouseup', this.onWindowMouseUp);
  }

  /** Als Feld gebunden, damit `removeEventListener` dieselbe Referenz sieht. */
  private readonly onWindowMouseMove = (e: MouseEvent): void => {
    if (!this.connectionDraft && !this.draggingNode) return;
    this.onCanvasMouseMove(e);
  };

  private readonly onWindowMouseUp = (): void => {
    this.onCanvasMouseUp();
  };

  /** Sync a rendered node position back to the manifest (StepNode.inputs.__ui or TriggerNode.config.__ui) */
  /**
   * Schreibt die aktuell gezeichneten Kanten in die `on*`-Felder des Manifests.
   *
   * Pendant zu {@link syncPositionToManifest}: das Manifest ist die Wahrheit, die Leinwand nur
   * seine Darstellung. Bewusst auch im read-only Modus erlaubt — dort ändert sich nichts an den
   * Kanten, der Aufruf ist dann ein No-op.
   */
  private syncManifestEdges(): void {
    if (!this.manifest) return;
    syncEdgesToManifest(this.manifest, this.edges, this.modules);
  }

  private syncPositionToManifest(nodeId: string, x: number, y: number): void {
    const triggerId = this.manifest.trigger?.nodeId ?? 'trigger_0';
    if (nodeId === triggerId) {
      if (!this.manifest.trigger!.config) this.manifest.trigger!.config = {};
      setNodePosition(this.manifest.trigger!.config!, x, y);
    } else {
      const step = (this.manifest.steps ?? []).find(s => s.nodeId === nodeId);
      if (step) {
        if (!step.inputs) step.inputs = {};
        setNodePosition(step.inputs, x, y);
      }
    }
  }

  /** Create a RenderedNode from a StepNode using module registry for enrichment */
  private createRenderedNodeFromStep(step: StepNode): RenderedNode {
    const mod = this.moduleMap.get(step.moduleId ?? '');
    const category = mapCategory(mod?.category ?? 'ACTION');
    const pos = getNodePosition(step.inputs);
    const name = getNodeName(step.inputs, mod);
    // Ein Flow-Eingang; rechts die Flow-Ausgänge (je Verzweigungs-Fall einer, settings-abhängig —
    // siehe resolveBranchSpec) und dahinter die benannten Wert-Ausgaben der Module, die das per
    // `x-outputs-as-ports` verlangen (Variable: je definierter Variablen eine).
    //
    // Wert-Ausgaben sind Anzeige, keine Ziehpunkte: Werte werden im Konfig-Panel gebunden, nicht
    // gezogen (siehe onOutputPortMouseDown).
    const inputPorts: NodePort[] = [{...FLOW_IN_PORT}];
    const outputPorts: NodePort[] = buildOutputPorts(mod, step.settings);

    // Konfigurations-Zusammenfassung im Kopf; sie kostet eine zweite Zeile, die in headerHeight
    // UND computeNodeHeight einfließen muss (sonst wandern die Portkoordinaten).
    const summary = nodeSummary(step, mod, ref => this.referenceLabel(ref));
    const headerHeight = headerHeightFor(!!summary);

    return {
      id: step.nodeId!,
      label: name,
      category,
      categoryLabel: CATEGORY_LABELS[category] ?? category,
      categoryAbbrev: CATEGORY_ABBREVS[category] ?? '?',
      categoryColor: CATEGORY_COLORS[category] ?? '#6B7280',
      nodeIcon: resolveModuleIcon(mod?.icon) ?? CATEGORY_ICONS[category] ?? null,
      x: pos.x, y: pos.y,
      width: NODE_WIDTH,
      height: computeNodeHeight(inputPorts, outputPorts, headerHeight),
      headerHeight,
      summary,
      inputPorts,
      outputPorts,
      stepRef: step,
    };
  }

  /**
   * Anzeigename für eine `${node.outputs.key}`-Referenz in der Node-Zusammenfassung: der Titel des
   * Quell-Ports, in Anführungszeichen. Ohne auflösbaren Titel `null` — dann entfällt die
   * Zusammenfassung, statt eine Roh-Referenz auf der Karte zu zeigen.
   */
  private referenceLabel(ref: string): string | null {
    const match = /^\$\{([^.}]+)\.outputs\.(.+)}$/.exec(ref.trim());
    if (!match) return null;
    const props = this.outputSchemaProps(match[1], this.moduleIdOf(match[1]));
    const title = props?.[match[2]]?.title;
    return `„${title ?? match[2]}"`;
  }

  /** Modul-ID eines Knotens (Trigger oder Schritt) im aktuellen Manifest. */
  private moduleIdOf(nodeId: string): string | undefined {
    if (this.manifest?.trigger?.nodeId === nodeId) return this.manifest.trigger.moduleId;
    return (this.manifest?.steps ?? []).find(s => s.nodeId === nodeId)?.moduleId;
  }

  /** Get the schema key (property name) for an output port at the given index */
  /**
   * Output-Schema-Properties eines Quellknotens. Für Trigger-Knoten gewinnt das dynamisch
   * gebaute `config.outputSchema` (z.B. Eingangsparameter: ein Port je konfiguriertem Parameter);
   * für Schritte werden dynamisch konfigurierte Ausgabeparameter (z.B. KI-Anweisung) eingemischt
   * (siehe {@link buildDataOutputSchema}).
   */
  private outputSchemaProps(nodeId: string | undefined, moduleId: string | undefined): { [key: string]: any } | null {
    const triggerConfig = this.manifest.trigger?.config;
    const isTriggerModule = moduleId === this.manifest.trigger?.moduleId;
    if (isTriggerModule && triggerConfig?.outputSchema?.properties) {
      return triggerConfig.outputSchema.properties;
    }
    const mod = this.moduleMap.get(moduleId ?? '');
    const stepSettings = (this.manifest.steps ?? []).find(s => s.nodeId === nodeId)?.settings;
    const props = buildDataOutputSchema(moduleId, stepSettings, mod?.outputSchema)?.properties;
    return props ?? null;
  }


  private rebuildEdges() {
    // Kanten SOFORT ins Manifest schreiben, nicht erst beim Speichern.
    //
    // Zwei Fehler hingen daran: (1) `rebuildCanvasFromManifest()` leitet die Kanten aus dem
    // Manifest ab — stand dort noch kein `onSuccess`/`onBranch`, war eine gerade gezogene
    // Verbindung weg, sobald irgendeine Einstellung die Ports verschob. (2) Der Wert-Picker im
    // Panel bestimmt seine Kandidaten über die Fluss-Vorgänger, und die stehen ebenfalls nur im
    // Manifest — ohne Sync blieb er beim Bearbeiten leer, obwohl die Verbindung sichtbar war.
    //
    // `rebuildEdges` ist der gemeinsame Endpunkt jeder diskreten Kanten- und Positionsänderung
    // (Ziehen, Löschen, Einfügen, Undo/Redo) und läuft NICHT pro Mausbewegung. Der Sync ist eine
    // reine Neuberechnung aus `this.edges` und damit idempotent.
    this.syncManifestEdges();

    this.renderedEdges = [];
    for (const edge of this.edges) {
      const source = this.nodeMap.get(edge.source);
      const target = this.nodeMap.get(edge.target);
      if (!source || !target) continue;
      const sourcePortIdx = Math.min(edge.sourcePortIndex, Math.max(source.outputPorts.length - 1, 0));
      const targetPortIdx = Math.min(edge.targetPortIndex, Math.max(target.inputPorts.length - 1, 0));
      const sp = getOutputPortPoint(source, sourcePortIdx);
      const tp = getInputPortPoint(target, targetPortIdx);

      this.renderedEdges.push({
        id: edge.id, sourceNodeId: edge.source, sourcePortIndex: sourcePortIdx,
        targetNodeId: edge.target, targetPortIndex: targetPortIdx,
        sourceX: sp.x, sourceY: sp.y, targetX: tp.x, targetY: tp.y,
        label: edge.label, type: edge.type,
        path: buildBezierPath(sp.x, sp.y, tp.x, tp.y),
      });
    }
  }

  private recalculatePortConnections() {
    for (const node of this.renderedNodes) {
      node.inputPorts.forEach(p => p.connected = false);
      node.outputPorts.forEach(p => p.connected = false);
    }
    for (const edge of this.edges) {
      const src = this.nodeMap.get(edge.source);
      if (src) {
        const idx = Math.min(edge.sourcePortIndex, Math.max(src.outputPorts.length - 1, 0));
        if (src.outputPorts[idx]) src.outputPorts[idx].connected = true;
      }
      const tgt = this.nodeMap.get(edge.target);
      if (tgt) {
        const idx = Math.min(edge.targetPortIndex, Math.max(tgt.inputPorts.length - 1, 0));
        if (tgt.inputPorts[idx]) tgt.inputPorts[idx].connected = true;
      }
    }
  }
}
